import React from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";

/**
 * In-dashboard wiki: how brands operate across Lusuku.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - No repo file paths or .md references — staff in production cannot
 *     open files; operational detail is inlined here instead.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#6b1a8a";
const TINT = "#6b1a8a14";
const PILLTEXT = "#6b1a8a";
const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };

const cardStyle = {
  border: "1px solid #e5e9f1",
  borderRadius: 8,
  boxShadow: "none",
};

const mutedText = { fontSize: 12.5, color: "#6b7a99" };

const pillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  background: TINT,
  color: PILLTEXT,
  marginRight: 6,
  marginBottom: 4,
};

const Pill = ({ label }) => <span style={pillStyle}>{label}</span>;

const SectionIcon = ({ cls }) => (
  <i
    className={cls}
    style={{ fontSize: 15, color: ACCENT, marginRight: 8, verticalAlign: "middle" }}
  />
);

const QA = ({ q, a }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
    <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 1 }}>{a}</div>
  </div>
);

const BrandPlatformWiki = () => {
  return (
    <div>
      {/* Compact header — the Knowledge Base container owns padding + tab label */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>Brands on Lusuku</div>
        <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 2 }}>
          Staff reference for brand storefronts, API behaviour, web checkout,
          delivery, 3PL, artwork escrow and refunds.
        </div>
      </div>

      {IS_DEV && (
        <div
          style={{
            background: "#fffdf5",
            border: "1px solid #f1e0a8",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12.5,
          }}
        >
          <div style={{ fontWeight: 700, color: "#7a5a00", marginBottom: 2 }}>
            Local development only
          </div>
          <div style={{ color: "#6b7a99" }}>
            Run the brand seed script — see the repo README — to populate three
            sample brand tenants (rice supplier, B2B electronics, packaging)
            with multi-section catalogues, outlets, featured images and
            landing-carousel visibility. The terminal output lists each seeded
            brand&rsquo;s <code>_id</code> — open{" "}
            <code>/brand/shop/&lt;id&gt;</code> on the web app to view. Exact
            command + credentials stay in the README (this UI is served to all
            supervisors in production).
          </div>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-badge" /> At a glance — what a brand actually is
          </Card.Title>
          <div className="mb-2">
            <Pill label="B2B-capable" />
            <Pill label="Multi-section catalogue" />
            <Pill label="Outlets" />
            <Pill label="Quote flow" />
            <Pill label="Tiered pricing" />
            <Pill label="Web-first shopper" />
          </div>
          <p style={{ ...mutedText, marginBottom: 0 }}>
            A <strong>Brand</strong> is a first-class tenant type, distinct
            from restaurants and retailers. Brands ship larger catalogues,
            support B2B pricing tiers and quote-driven orders, and are sold via
            a web-first storefront (the Expo app does not embed brand
            shopping). Each brand has its own catalogue of sections and
            products, fulfilment hints, and a linked <em>owner</em> account.
          </p>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-badge" /> 1. Onboarding vs vendors
              </Card.Title>
              <Card.Text style={{ ...mutedText, marginBottom: 0 }}>
                Public vendor registration creates RESTAURANT / RETAILER /
                WHOLESALER / HEALTHCARE sellers — <strong>not</strong> brand
                records. A <strong>Brand</strong> is created by platform staff
                in <strong>BRANDS → Manage Brands</strong>, either linking an
                existing <em>owner</em> user id or leaving owner blank so the
                server auto-links or creates an owner account from the brand
                email (promoted to <code>BRAND_OWNER</code>). New owners
                receive a password setup link by email; staff can resend it
                from the brand row actions, which can also repair legacy rows
                with a missing or invalid owner link.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="h-100" style={cardStyle}>
            <Card.Body>
              <Card.Title style={titleStyle}>
                <SectionIcon cls="nc-icon nc-lock-circle-open" /> 2. Catalogue &amp; auth
              </Card.Title>
              <Card.Text style={{ ...mutedText, marginBottom: 0 }}>
                Anonymous callers hitting the brand detail endpoint receive a{" "}
                <strong>teaser</strong> — brand metadata only, no product rows
                (<code>catalogueLocked: true</code>, reason{" "}
                <code>LOGIN_REQUIRED</code>; platform rule: know who is
                browsing). Logged-in shoppers receive the full catalogue with
                sections, SKUs, and tier/quote pricing applied server-side.
                Internal-only commercial fields are stripped for
                non-super-admin API consumers.
              </Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" /> 3. Dashboard surface area
          </Card.Title>
          <p style={{ ...mutedText, marginBottom: 12 }}>
            Everything brand-related that a supervisor can touch, with the
            path in the admin sidebar.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 12 }}>
            <thead style={{ background: "#f5f7fb" }}>
              <tr>
                <th>Sidebar</th>
                <th>Path</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Manage Brands</td>
                <td><code>/admin/brands-management</code></td>
                <td className="text-muted">
                  Profile, B2B flows, colours, owner provisioning, internal
                  commercial (super)
                </td>
              </tr>
              <tr>
                <td>Brand B2B Quotes</td>
                <td><code>/admin/brand-b2b-quotes</code></td>
                <td className="text-muted">Quote pipeline across all brands</td>
              </tr>
              <tr>
                <td>Brand Orders</td>
                <td><code>/admin/brand-orders</code></td>
                <td className="text-muted">
                  Brand-tagged orders only — KPI cards + status filter pills
                </td>
              </tr>
              <tr>
                <td>Products / outlets</td>
                <td>
                  <code>/admin/brand-product-management</code> (hidden),{" "}
                  <code>/admin/brand-outlet-management</code>
                </td>
                <td className="text-muted">Sections, SKUs, pickup outlets</td>
              </tr>
              <tr>
                <td>Featured images</td>
                <td><code>/admin/featured-image-upload</code></td>
                <td className="text-muted">Landing carousel assets</td>
              </tr>
            </tbody>
          </Table>
          <p style={{ ...mutedText, marginTop: 10, marginBottom: 0 }}>
            Brand owners (<code>operationMode: BRAND_OWNER</code>) get their
            own scoped views of the same surfaces: dashboard stats, shop
            orders, catalog, quotes, profile/settings and wallet.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-tv-2" /> 4. Web shopper flow
          </Card.Title>
          <p style={{ ...mutedText, marginBottom: 8 }}>
            Brand shopping happens on the <strong>web</strong> storefront
            (Netlify-hosted), not the Expo mobile app. A customer landing on a
            brand page sees a teaser until they sign in; the signed-in view
            shows the full catalogue with per-brand cart and tier-aware
            pricing.
          </p>
          <ul style={{ ...mutedText, paddingLeft: 18, marginBottom: 0 }}>
            <li>Each brand has its own cart, independent of the main cart.</li>
            <li>
              Checkout creates a brand order on the main orders API, tagged
              with <code>isBrandOrder</code> and <code>brandId</code> so
              reporting and wallet rules know to treat it as a brand sale.
            </li>
            <li>
              SEO / link-preview metadata is injected at deploy time so a
              brand page shared on WhatsApp or social shows a rich card.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" /> 5. Orders, delivery &amp; 3PL
          </Card.Title>
          <p style={{ ...mutedText, marginBottom: 8 }}>
            Brand orders follow the same state machine as every other order on
            Lusuku — they just carry an extra tag so reporting, wallet rules,
            and 3PL dispatch can treat them correctly.
          </p>
          <ul style={{ ...mutedText, paddingLeft: 18, marginBottom: 0 }}>
            <li>
              <strong>Vendor resolution.</strong> The orders pipeline resolves
              the seller as a restaurant, retailer, or <strong>brand</strong>,
              picking up hours, name, and pickup coordinates uniformly.
            </li>
            <li>
              <strong>Pickup coordinates.</strong> Brand pickup lat/lng live
              on the brand record and must be set by the owner under{" "}
              <strong>Account → Profile → Pickup / Warehouse Address</strong>{" "}
              before distance-based delivery fees and route maps work. Order
              detail resolves brand coordinates from the order&rsquo;s{" "}
              <code>brandId</code> as a single authoritative lookup.
            </li>
            <li>
              <strong>3PL dispatch.</strong> Brand orders can be handed to a
              3PL partner exactly like other verticals — staff assign a
              partner while the order is <code>PROCESSING</code>
              (workflow-driven, not special-cased; same{" "}
              <code>delivery_status</code> / <code>PENDING_3PL</code> fields).
            </li>
            <li>
              <strong>Escrow.</strong> Brand sales settle through the same
              Wallet Service escrow that retail / restaurant sales use; the
              commission rate may differ per contract.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" /> 6. Artwork escrow (branded packaging)
          </Card.Title>
          <p style={{ ...mutedText, marginBottom: 8 }}>
            Branded-packaging orders are created with flow type{" "}
            <code>BRANDED_PACKAGING</code>, which starts the order at stage{" "}
            <code>AWAITING_ARTWORK</code>. Funds are held in escrow until the
            artwork is approved and the physical run ships.
          </p>
          <ul style={{ ...mutedText, paddingLeft: 18, marginBottom: 0 }}>
            <li>
              <strong>Buyer uploads artwork.</strong> The buyer attaches
              artwork file metadata (file URL, id, original name) to the order
              via the brand orders artwork endpoint.
            </li>
            <li>
              <strong>Review decision.</strong> The brand owner or an admin
              reviews and records <code>APPROVED</code> or{" "}
              <code>REJECTED</code>, optionally with a reason code and note.
            </li>
            <li>
              <strong>Rejection reverses escrow.</strong> If artwork is
              rejected while escrow is <code>HELD</code>, the escrow status
              flips to <code>REVERSED</code>. The money is released from the
              vendor-payout path, but the <strong>customer refund is a
              separate operational step</strong> — it does not fire
              automatically. Use the Customer Refund flow (section 7).
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-refresh-69" /> 7. Refunding brand customers
          </Card.Title>
          <p style={{ ...mutedText, marginBottom: 8 }}>
            Refunds (rejected artwork, cancelled or disputed orders,
            mischarges) are issued from the <strong>Customer Refund</strong>{" "}
            form as direct MTN MoMo disbursements — not the normal auto-refund
            path. The disbursement runs through the Wallet Service with the
            same retry logic and callbacks as vendor payouts.
          </p>
          <ul style={{ ...mutedText, paddingLeft: 18, marginBottom: 8 }}>
            <li>
              <strong>Find the order</strong> by full or partial order id
              (paste from the Orders table — a leading <code>#</code> is
              stripped), customer name, or phone. The preview panel shows
              customer, vendor, 3PL partner, total, payment method and current
              refund status.
            </li>
            <li>
              <strong>Amount rules:</strong> minimum UGX 10,000, never more
              than the order total. The form pre-fills the order total.
            </li>
            <li>
              <strong>MTN numbers only.</strong> The phone field detects the
              carrier as you type; Airtel or unknown prefixes block
              submission — obtain an MTN number from the customer first.
            </li>
            <li>
              <strong>One refund at a time.</strong> An order already{" "}
              <code>PENDING</code> or <code>REFUNDED</code> cannot be
              refunded again; a <code>REFUND_FAILED</code> order can be
              retried by submitting the form again.
            </li>
          </ul>
          <Table responsive size="sm" className="mb-0" style={{ fontSize: 12 }}>
            <thead style={{ background: "#f5f7fb" }}>
              <tr>
                <th>Refund status</th>
                <th>Meaning</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>NONE</code></td>
                <td className="text-muted">No refund initiated</td>
              </tr>
              <tr>
                <td><code>PENDING</code></td>
                <td className="text-muted">Disbursement queued, awaiting MTN MoMo confirmation</td>
              </tr>
              <tr>
                <td><code>REFUNDED</code></td>
                <td className="text-muted">MTN transfer confirmed successful</td>
              </tr>
              <tr>
                <td><code>REFUND_FAILED</code></td>
                <td className="text-muted">All MTN retries exhausted — admin may retry</td>
              </tr>
            </tbody>
          </Table>
          <p style={{ ...mutedText, marginTop: 10, marginBottom: 0 }}>
            Status changes push live to the admin dashboard, the vendor, any
            assigned 3PL partner, and the customer&rsquo;s app — customers see
            a refund pill on the order card and a refund section (amount +
            note) on the order detail screen.
          </p>
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" /> Quick answers
          </Card.Title>
          <QA
            q="A company wants a brand storefront — where do I start?"
            a="BRANDS → Manage Brands. Create the brand profile with its contact email; link an existing owner user id, or leave owner blank and the server links/creates the owner account and emails a password setup link."
          />
          <QA
            q="The brand owner never got their setup email."
            a="Use the Resend owner setup link action on the brand row in Manage Brands. It can also repair older brands whose owner link is missing by re-linking from the stored brand email."
          />
          <QA
            q="A shopper says the brand page shows no products."
            a="They are not signed in — anonymous visitors get a teaser only. Ask them to log in on the web storefront; the full catalogue and prices then load."
          />
          <QA
            q="Delivery fee or route map is wrong / missing for a brand order."
            a="Check the brand's pickup coordinates: the owner must set them under Account → Profile → Pickup / Warehouse Address. Without lat/lng, distance-based fees cannot be computed."
          />
          <QA
            q="A customer rejected the packaging artwork — did they get their money back?"
            a="Not automatically. Rejection reverses the escrow hold, but the customer refund is a separate step: issue it from the Customer Refund form as an MTN MoMo disbursement."
          />
          <QA
            q="A refund shows REFUND_FAILED — now what?"
            a="Verify the customer's MTN number, then submit the refund form again. FAILED orders can be retried; PENDING or REFUNDED ones cannot."
          />
          <QA
            q="Can the customer's refund go to an Airtel number?"
            a="No. Refund disbursements ride the MTN MoMo rail only — the form blocks Airtel and unknown prefixes. Ask the customer for an MTN number."
          />
          {IS_DEV && (
            <div className="mt-3">
              <Badge bg="secondary">DEV</Badge>{" "}
              <span style={mutedText}>
                Three seeded brand scenarios are available locally for UI
                testing: a staples supplier (SINGLE pricing), a B2B
                electronics tenant (TIERED pricing), and a print/packaging
                tenant (artwork + quote flow). Run the brand seed script —
                see the repo README for the command.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default BrandPlatformWiki;
